/**
 * Feature Flag Context Extractor
 * Observability & Analytics Infrastructure - Task 1.3
 *
 * Extracts feature flag state for error and analytics context.
 * Includes only enabled flags to reduce noise.
 *
 * Backend: caller passes backend/src/config/feature-flags.ts's getAllFlags()
 * result into getBackendFeatureFlags() below (a same-package cross-import
 * from here can't resolve the backend's `#/` import-map scope).
 * Frontend: reads from frontend/src/services/feature-flags.ts getAllFlags()
 */

/**
 * Feature flag context format
 */
export interface FeatureFlagContext {
  flags: Record<string, boolean>;
}

function resolveEnvObject(): Record<string, string | undefined> {
  const g = globalThis as unknown as {
    Deno?: { env?: { toObject?: () => Record<string, string> } };
    process?: { env?: Record<string, string | undefined> };
  };

  try {
    const denoEnv = g.Deno?.env?.toObject?.();
    if (denoEnv && typeof denoEnv === "object") return denoEnv;
  } catch {
    // Deno env access may be denied (permissions). Fall back to other sources.
  }

  const nodeEnv = g.process?.env;
  if (nodeEnv && typeof nodeEnv === "object") return nodeEnv;

  return {};
}

function parseBool(value: string | undefined): boolean {
  const normalized = (value ?? "").trim().toLowerCase();
  return normalized === "1" || normalized === "true" || normalized === "yes" ||
    normalized === "y" || normalized === "on";
}

/**
 * Extract backend feature flags (Deno/Node environment)
 *
 * @param allFlags Pre-computed flag registry, e.g. backend's own
 *   `getAllFlags()`. When omitted, falls back to scanning FF_* env vars
 *   directly, which misses any flag that is enabled by default rather than
 *   via an explicit env override.
 * @returns Feature flag context with enabled flags only
 */
export async function getBackendFeatureFlags(
  allFlags?: Record<string, { enabled: boolean }>,
): Promise<FeatureFlagContext> {
  try {
    if (allFlags) {
      const enabledFlags: Record<string, boolean> = {};
      for (const [name, config] of Object.entries(allFlags)) {
        if (config.enabled) {
          enabledFlags[`FF_${name}`] = true;
        }
      }
      return { flags: enabledFlags };
    }

    const env = resolveEnvObject();
    const enabledFlags: Record<string, boolean> = {};
    for (const [key, value] of Object.entries(env)) {
      if (!key.startsWith("FF_")) continue;
      if (parseBool(value)) enabledFlags[key] = true;
    }
    return { flags: enabledFlags };
  } catch (error) {
    // Fail gracefully if flag resolution throws unexpectedly
    console.warn("Failed to load backend feature flags:", error);
    return { flags: {} };
  }
}

/**
 * Extract frontend feature flags (browser environment)
 * Reads VITE_FF_* build-time variables
 *
 * @returns Feature flag context with enabled flags only
 */
export async function getFrontendFeatureFlags(): Promise<FeatureFlagContext> {
  try {
    // Vite exposes build-time env vars on import.meta.env in the browser bundle.
    // Scan for any enabled VITE_FF_* flags without importing frontend internals.
    const env = (import.meta as unknown as { env?: Record<string, unknown> })
      .env;
    const enabledFlags: Record<string, boolean> = {};

    if (env && typeof env === "object") {
      for (const [key, value] of Object.entries(env)) {
        if (!key.startsWith("VITE_FF_")) continue;
        if (value === true || value === "true" || value === "1") {
          enabledFlags[key] = true;
        }
      }
    }

    return { flags: enabledFlags };
  } catch (error) {
    // Fail gracefully if feature-flags module not available
    console.warn("Failed to load frontend feature flags:", error);
    return { flags: {} };
  }
}

/**
 * Auto-detect environment and extract appropriate feature flags
 *
 * @returns Feature flag context for current environment
 */
export async function getFeatureFlagContext(): Promise<FeatureFlagContext> {
  // Detect environment based on available globals
  // Check for browser first to avoid polyfill issues (process is often shimmed in browsers)
  if (typeof window !== "undefined") {
    // Frontend environment (browser)
    return getFrontendFeatureFlags();
  }

  // Use globalThis to check for Deno/Node without direct reference
  const g = globalThis as unknown as { Deno?: unknown; process?: unknown };
  const hasDeno = typeof g.Deno !== "undefined";
  const hasProcess = typeof g.process !== "undefined";

  if (hasDeno || hasProcess) {
    // Backend environment (Deno or Node)
    return getBackendFeatureFlags();
  }

  // Unknown environment
  return { flags: {} };
}
